import React, { useState, useEffect } from 'react';
import { Settings, Send, Image as ImageIcon, User, Clock, Video, Eye, ChevronDown, ChevronUp, Save, FileText, Sparkles, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { getAllPlatforms } from './firebase/platformService';
import { saveTemplate, getAllTemplates, deleteTemplate } from './firebase/templateService';
import { logoutUser } from './firebase/authService';
import { postToMultiplePlatforms } from './firebase/postingService';
import { uploadImageToStorage, uploadMultipleImages, uploadMultipleMedia, deleteMultipleImages } from './firebase/storageService';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from './firebase/config';
import SettingsModal from './components/SettingsModal';
import PlatformPreviewModal from './components/PlatformPreviewModal';
import TemplateCard from './components/TemplateCard';
import AIContentModal from './components/AIContentModal';
import InstagramSetupModal from './components/InstagramSetupModal';
import * as oauthService from './firebase/oauthService';

const Dashboard = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);

  // İçerik
  const [content, setContent] = useState('');
  const [media, setMedia] = useState([]);

  // Seçili platformlar
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  // Platform özelleştirme
  const [platformCustomization, setPlatformCustomization] = useState({});

  // Önizleme
  const [previewPlatform, setPreviewPlatform] = useState(null);

  // Accordion - açık platformlar
  const [expandedPlatforms, setExpandedPlatforms] = useState([]);

  // Zamanla paylaş (şimdilik devre dışı)
  const [scheduleEnabled, setScheduleEnabled] = useState(false);

  // Şablonlar
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // AI Modal
  const [showAIModal, setShowAIModal] = useState(false);

  // Instagram Setup Modal
  const [showInstagramSetup, setShowInstagramSetup] = useState(false);
  const [instagramSetupError, setInstagramSetupError] = useState(null);

  // Posting state
  const [isPosting, setIsPosting] = useState(false);
  const [postingProgress, setPostingProgress] = useState('');
  const [postingResults, setPostingResults] = useState(null);

  // İstatistikler
  const [totalPosts, setTotalPosts] = useState(0);

  // Platform bilgileri
  const allPlatforms = [
    { id: 'instagram', name: 'Instagram', icon: '📷', logo: '/logos/instagram.png', color: '#E4405F' },
    { id: 'facebook', name: 'Facebook', icon: '👍', logo: '/logos/facebook.png', color: '#1877F2' },
    { id: 'twitter', name: 'Twitter (X)', icon: '🐦', logo: '/logos/twitter.png', color: '#1DA1F2' },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', logo: '/logos/linkedin.png', color: '#0A66C2' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', logo: '/logos/tiktok.png', color: '#000000' },
    { id: 'youtube', name: 'YouTube', icon: '▶️', logo: '/logos/youtube.png', color: '#FF0000' },
    { id: 'pinterest', name: 'Pinterest', icon: '📌', logo: '/logos/pinterest.png', color: '#E60023' },
    { id: 'snapchat', name: 'Snapchat', icon: '👻', logo: '/logos/snapchat.png', color: '#FFFC00' },
    { id: 'reddit', name: 'Reddit', icon: '🤖', logo: '/logos/reddit.png', color: '#FF4500' },
    { id: 'tumblr', name: 'Tumblr', icon: '📝', logo: '/logos/tumblr.png', color: '#35465C' },
    { id: 'whatsapp', name: 'WhatsApp Business', icon: '💬', logo: '/logos/whatsapp.png', color: '#25D366' },
    { id: 'telegram', name: 'Telegram', icon: '✈️', logo: '/logos/telegram.png', color: '#0088cc' },
    { id: 'discord', name: 'Discord', icon: '🎮', logo: '/logos/discord.png', color: '#5865F2' },
    { id: 'threads', name: 'Threads', icon: '🧵', logo: '/logos/threads.png', color: '#000000' },
    { id: 'mastodon', name: 'Mastodon', icon: '🐘', logo: '/logos/mastodon.png', color: '#6364FF' },
    { id: 'bluesky', name: 'Bluesky', icon: '🦋', logo: '/logos/bluesky.png', color: '#1185FE' }
  ];

  useEffect(() => {
    if (currentUser) {
      loadConnectedPlatforms();
      loadTemplates();
      loadTotalPosts();

      // Instagram setup hatasını kontrol et (OAuth callback'ten gelen)
      const instagramError = localStorage.getItem('instagram_setup_error');
      if (instagramError) {
        try {
          const errorData = JSON.parse(instagramError);
          setInstagramSetupError(errorData);
          setShowInstagramSetup(true);
          // Hata mesajını temizle
          localStorage.removeItem('instagram_setup_error');
        } catch (e) {
          console.error('Instagram setup error parse hatası:', e);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadConnectedPlatforms = async () => {
    if (currentUser) {
      const result = await getAllPlatforms(currentUser.uid);
      console.log('🔍 loadConnectedPlatforms - Firestore\'dan gelen:', result.data);
      if (result.success) {
        // Kullanıcının eklediği TÜM platformlar (bağlı ve bağlı olmayan)
        setConnectedPlatforms(result.data);
        console.log('✅ connectedPlatforms state güncellendi:', result.data.length, 'platform');

        // Sadece bağlı olanları varsayılan seçili yap
        const connectedIds = result.data.filter(p => p.enabled && p.token).map(p => p.id);
        setSelectedPlatforms(connectedIds);

        // Platform özelleştirme state'ini başlat
        const customization = {};
        result.data.forEach(platform => {
          customization[platform.id] = {
            useMainContent: true,
            customContent: '',
            useMainImages: true,
            customImages: [],
            postTypes: platform.id === 'instagram' ? ['feed'] : ['feed'], // Array of selected post types
            selectedStoryMediaIndex: 0
          };
        });
        setPlatformCustomization(customization);
      }
    }
  };

  const handlePlatformsUpdate = async (platforms) => {
    console.log('🔄 handlePlatformsUpdate çağrıldı, gelen platformlar:', platforms);
    // Kullanıcının eklediği TÜM platformlar
    setConnectedPlatforms(platforms);
    console.log('✅ connectedPlatforms state güncellendi:', platforms.length, 'platform');

    // Sadece bağlı olanları seçili yap
    const connectedIds = platforms.filter(p => p.enabled && p.token).map(p => p.id);
    setSelectedPlatforms(connectedIds);

    // Platform özelleştirme state'ini güncelle
    const customization = {};
    platforms.forEach(platform => {
      customization[platform.id] = platformCustomization[platform.id] || {
        useMainContent: true,
        customContent: '',
        useMainImages: true,
        customImages: [],
        postTypes: platform.id === 'instagram' ? ['feed'] : ['feed'], // Array of selected post types
        selectedStoryMediaIndex: 0
      };
    });
    setPlatformCustomization(customization);
  };

  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);

    files.forEach(file => {
      // Video ise validasyon yap
      if (file.type.startsWith('video')) {
        // Boyut kontrolü (1GB = 1024 * 1024 * 1024 bytes)
        const maxSize = 1024 * 1024 * 1024; // 1GB
        if (file.size > maxSize) {
          alert(t('dashboard.errors.videoTooLarge', {
            name: file.name,
            maxSize: '1GB',
            actualSize: `${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`
          }));
          return;
        }

        // Süre kontrolü için video element oluştur
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';

        videoElement.onloadedmetadata = () => {
          window.URL.revokeObjectURL(videoElement.src);

          const duration = videoElement.duration; // saniye cinsinden
          const maxDuration = 90; // Instagram Reels max 90 saniye

          if (duration > maxDuration) {
            alert(t('dashboard.errors.videoTooLong', {
              name: file.name,
              maxDuration: maxDuration,
              maxTime: `${Math.floor(maxDuration / 60)}:${maxDuration % 60}`,
              actualDuration: Math.floor(duration),
              actualTime: `${Math.floor(duration / 60)}:${Math.floor(duration % 60)}`
            }));
            return;
          }

          // Validasyon başarılı, video'yu ekle
          const reader = new FileReader();
          reader.onloadend = () => {
            setMedia(prev => [...prev, {
              url: reader.result,
              type: 'video',
              name: file.name,
              duration: Math.floor(duration),
              size: file.size
            }]);
          };
          reader.readAsDataURL(file);
        };

        videoElement.src = URL.createObjectURL(file);
      } else {
        // Görsel için sadece boyut kontrolü
        const maxImageSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxImageSize) {
          alert(t('dashboard.errors.imageTooLarge', {
            name: file.name,
            maxSize: '10MB',
            actualSize: `${(file.size / (1024 * 1024)).toFixed(2)}MB`
          }));
          return;
        }

        // Görseli ekle ve boyutlarını al
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const aspectRatio = (width / height).toFixed(2);

            console.log('📸 Görsel yüklendi:', {
              name: file.name,
              width,
              height,
              aspectRatio,
              size: `${(file.size / 1024).toFixed(0)}KB`
            });

            // Instagram feed için uygun mu kontrol et
            const minRatio = 0.8; // 4:5 portrait
            const maxRatio = 1.91; // 1.91:1 landscape
            const isValidForFeed = aspectRatio >= minRatio && aspectRatio <= maxRatio;
            const isValidForStory = aspectRatio >= 0.5 && aspectRatio <= 0.6; // 9:16 civarı

            if (!isValidForFeed && !isValidForStory) {
              console.warn('⚠️ Görsel Instagram için uygun olmayabilir!', {
                aspectRatio,
                feedRange: `${minRatio} - ${maxRatio}`,
                storyRange: '0.5 - 0.6 (9:16)'
              });
            }

            setMedia(prev => [...prev, {
              url: reader.result,
              type: 'image',
              name: file.name,
              size: file.size,
              width,
              height,
              aspectRatio,
              isValidForFeed,
              isValidForStory
            }]);
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeMedia = (index) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleCustomMediaUpload = (platformId, e) => {
    const files = Array.from(e.target.files);
    const newMedia = [];
    let processedCount = 0;

    files.forEach(file => {
      // Video ise validasyon yap
      if (file.type.startsWith('video')) {
        // Boyut kontrolü
        const maxSize = 1024 * 1024 * 1024; // 1GB
        if (file.size > maxSize) {
          alert(t('dashboard.errors.videoTooLarge', {
            name: file.name,
            maxSize: '1GB',
            actualSize: `${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`
          }));
          processedCount++;
          return;
        }

        // Süre kontrolü
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';

        videoElement.onloadedmetadata = () => {
          window.URL.revokeObjectURL(videoElement.src);

          const duration = videoElement.duration;
          const maxDuration = 90;

          if (duration > maxDuration) {
            alert(t('dashboard.errors.videoTooLong', {
              name: file.name,
              maxDuration: maxDuration,
              maxTime: `${Math.floor(maxDuration / 60)}:${maxDuration % 60}`,
              actualDuration: Math.floor(duration),
              actualTime: `${Math.floor(duration / 60)}:${Math.floor(duration % 60)}`
            }));
            processedCount++;
            if (processedCount === files.length && newMedia.length > 0) {
              setPlatformCustomization(prev => ({
                ...prev,
                [platformId]: {
                  ...prev[platformId],
                  customImages: [...(prev[platformId]?.customImages || []), ...newMedia]
                }
              }));
            }
            return;
          }

          // Validasyon başarılı
          const reader = new FileReader();
          reader.onloadend = () => {
            newMedia.push({
              url: reader.result,
              type: 'video',
              name: file.name,
              duration: Math.floor(duration),
              size: file.size
            });
            processedCount++;
            if (processedCount === files.length && newMedia.length > 0) {
              setPlatformCustomization(prev => ({
                ...prev,
                [platformId]: {
                  ...prev[platformId],
                  customImages: [...(prev[platformId]?.customImages || []), ...newMedia]
                }
              }));
            }
          };
          reader.readAsDataURL(file);
        };

        videoElement.src = URL.createObjectURL(file);
      } else {
        // Görsel validasyonu
        const maxImageSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxImageSize) {
          alert(t('dashboard.errors.imageTooLarge', {
            name: file.name,
            maxSize: '10MB',
            actualSize: `${(file.size / (1024 * 1024)).toFixed(2)}MB`
          }));
          processedCount++;
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const aspectRatio = (width / height).toFixed(2);

            console.log('📸 Custom görsel yüklendi:', {
              platform: platformId,
              name: file.name,
              width,
              height,
              aspectRatio
            });

            const minRatio = 0.8;
            const maxRatio = 1.91;
            const isValidForFeed = aspectRatio >= minRatio && aspectRatio <= maxRatio;
            const isValidForStory = aspectRatio >= 0.5 && aspectRatio <= 0.6;

            newMedia.push({
              url: reader.result,
              type: 'image',
              name: file.name,
              size: file.size,
              width,
              height,
              aspectRatio,
              isValidForFeed,
              isValidForStory
            });
            processedCount++;
            if (processedCount === files.length && newMedia.length > 0) {
              setPlatformCustomization(prev => ({
                ...prev,
                [platformId]: {
                  ...prev[platformId],
                  customImages: [...(prev[platformId]?.customImages || []), ...newMedia]
                }
              }));
            }
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeCustomMedia = (platformId, index) => {
    setPlatformCustomization(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        customImages: prev[platformId].customImages.filter((_, i) => i !== index)
      }
    }));
  };

  const togglePlatformContent = (platformId, field) => {
    setPlatformCustomization(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        [field]: !prev[platformId][field]
      }
    }));
  };

  const updateCustomContent = (platformId, text) => {
    setPlatformCustomization(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        customContent: text
      }
    }));
  };

  const toggleInstagramPostType = (platformId, postType) => {
    setPlatformCustomization(prev => {
      const current = prev[platformId]?.postTypes || ['feed'];
      let newPostTypes;

      if (current.includes(postType)) {
        // Remove if already selected
        newPostTypes = current.filter(t => t !== postType);
        // Ensure at least one is always selected
        if (newPostTypes.length === 0) {
          newPostTypes = [postType === 'feed' ? 'story' : 'feed'];
        }
      } else {
        // Add if not selected
        newPostTypes = [...current, postType];
      }

      return {
        ...prev,
        [platformId]: {
          ...prev[platformId],
          postTypes: newPostTypes
        }
      };
    });
  };

  const updateStoryMediaIndex = (platformId, index) => {
    setPlatformCustomization(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        selectedStoryMediaIndex: index
      }
    }));
  };

  const getContentForPlatform = (platformId) => {
    const custom = platformCustomization[platformId];
    if (!custom) return { content, images: media };

    return {
      content: custom.useMainContent ? content : custom.customContent,
      images: custom.useMainImages ? media : custom.customImages
    };
  };

  const handlePreview = (platformId) => {
    const platformData = connectedPlatforms.find(p => p.id === platformId);
    setPreviewPlatform(platformData || { id: platformId });
  };

  const togglePlatformExpand = (platformId) => {
    setExpandedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleConnectPlatform = async (platformId) => {
    try {
      let result;
      switch (platformId) {
        case 'instagram':
          result = await oauthService.connectInstagram(currentUser.uid);
          break;
        case 'facebook':
          result = await oauthService.connectFacebook(currentUser.uid);
          break;
        case 'twitter':
          result = await oauthService.connectTwitter(currentUser.uid);
          break;
        case 'linkedin':
          result = await oauthService.connectLinkedIn(currentUser.uid);
          break;
        case 'tiktok':
          result = await oauthService.connectTikTok(currentUser.uid);
          break;
        case 'youtube':
          result = await oauthService.connectYouTube(currentUser.uid);
          break;
        case 'pinterest':
          result = await oauthService.connectPinterest(currentUser.uid);
          break;
        case 'tumblr':
        case 'snapchat':
        case 'reddit':
        case 'whatsapp':
        case 'telegram':
        case 'discord':
        case 'threads':
        case 'mastodon':
        case 'bluesky':
          const platformName = allPlatforms.find(p => p.id === platformId)?.name || platformId;
          alert(t('dashboard.warnings.platformComingSoon', { platform: platformName }));
          return;
        default:
          console.error('Bilinmeyen platform:', platformId);
          return;
      }

      if (result.success) {
        // Popup kapandı, şimdi gerçekten bağlanıp bağlanmadığını kontrol et
        await loadConnectedPlatforms();

        // Biraz bekle ki Firestore güncellensin
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Platformu tekrar yükle ve kontrol et
        const platformResult = await getAllPlatforms(currentUser.uid);
        if (platformResult.success) {
          const platform = platformResult.data.find(p => p.id === platformId);

          if (platform && platform.enabled && platform.token) {
            // Gerçekten bağlandı
            alert(t('dashboard.success.platformConnected', { platform: platformId }));
          } else {
            // Bağlanmadı - kullanıcı muhtemelen iptal etti
            alert(t('dashboard.warnings.connectionIncomplete', { platform: platformId }));
          }
        }
      } else {
        alert(t('dashboard.errors.connectionError', { error: result.error }));
      }
    } catch (error) {
      console.error('Platform bağlama hatası:', error);

      // Instagram için özel hata handling
      if (platformId === 'instagram' && error.error) {
        if (error.error === 'NO_FACEBOOK_PAGE' || error.error === 'NO_INSTAGRAM_ON_PAGE') {
          setInstagramSetupError({
            errorType: error.error,
            message: error.message,
            pages: error.pages,
            setupUrl: error.setupUrl
          });
          setShowInstagramSetup(true);
          return;
        }
      }

      // Diğer hatalar için genel mesaj
      if (error.message) {
        alert(t('dashboard.errors.connectionFailed', { message: error.message }));
      } else if (error.error) {
        alert(t('dashboard.errors.genericError', { error: error.error }));
      } else {
        alert(t('dashboard.errors.tryAgain'));
      }
    }
  };

  const handlePost = async () => {
    if (!content.trim() && media.length === 0) {
      alert(t('dashboard.errors.noContent'));
      return;
    }

    if (selectedPlatforms.length === 0) {
      alert(t('dashboard.errors.noPlatformSelected'));
      return;
    }

    // Instagram için görsel kontrolü
    if (selectedPlatforms.includes('instagram') && media.length === 0) {
      alert(t('dashboard.errors.instagramRequiresMedia'));
      return;
    }

    // Instagram için aspect ratio kontrolü
    if (selectedPlatforms.includes('instagram') && media.length > 0) {
      const custom = platformCustomization['instagram'];
      const postTypes = custom?.postTypes || ['feed'];

      console.log('🔍 Instagram aspect ratio kontrolü:', {
        postTypes,
        mediaCount: media.length
      });

      // Feed seçiliyse aspect ratio kontrolü yap
      if (postTypes.includes('feed')) {
        const invalidFeedMedia = media.filter(m =>
          m.type === 'image' &&
          m.aspectRatio &&
          !m.isValidForFeed
        );

        if (invalidFeedMedia.length > 0) {
          const mediaInfo = invalidFeedMedia.map(m =>
            `${m.name}: ${m.width}x${m.height} (oran: ${m.aspectRatio})`
          ).join('\n');

          const confirmMsg = `⚠️ UYARI: Aşağıdaki görseller Instagram Feed için uygun boyutta değil!\n\n${mediaInfo}\n\nInstagram Feed kabul edilen oranlar:\n- Dikey (Portrait): En fazla 4:5 (0.80)\n- Kare (Square): 1:1 (1.00)\n- Yatay (Landscape): En fazla 1.91:1 (1.91)\n\nGörseller Instagram tarafından reddedilebilir.\n\nYine de paylaşmayı denemek istiyor musunuz?`;

          if (!window.confirm(confirmMsg)) {
            setIsPosting(false);
            return;
          }
        }
      }

      // Story seçiliyse aspect ratio uyarısı (zorunlu değil, sadece bilgi)
      if (postTypes.includes('story')) {
        const invalidStoryMedia = media.filter(m =>
          m.type === 'image' &&
          m.aspectRatio &&
          !m.isValidForStory
        );

        if (invalidStoryMedia.length > 0) {
          console.warn('⚠️ Bazı görseller Instagram Story için ideal değil (önerilen: 9:16)', invalidStoryMedia);
        }
      }
    }

    // Sadece bağlı platformları filtrele
    const connectedSelectedPlatforms = selectedPlatforms.filter(platformId => {
      const platform = connectedPlatforms.find(p => p.id === platformId);
      return platform && platform.enabled && platform.token;
    });

    if (connectedSelectedPlatforms.length === 0) {
      alert(t('dashboard.errors.noConnectedPlatforms'));
      return;
    }

    if (connectedSelectedPlatforms.length < selectedPlatforms.length) {
      const notConnectedCount = selectedPlatforms.length - connectedSelectedPlatforms.length;
      const confirm = window.confirm(t('dashboard.warnings.someNotConnected', {
        notConnected: notConnectedCount,
        connected: connectedSelectedPlatforms.length
      }));
      if (!confirm) return;
    }

    // Loading state başlat
    setIsPosting(true);
    setPostingProgress(t('dashboard.posting.sharing', { count: connectedSelectedPlatforms.length }));

    // Storage temizliği için path'leri saklayalım (scope dışında)
    let uploadedFilePaths = [];

    try {
      // 1. Medya varsa Firebase Storage'a yükle
      let imageUrl = null;
      let imageUrls = [];
      let videoUrl = null;

      if (media.length > 0) {
        setPostingProgress(t('dashboard.posting.uploading', { count: media.length }));
        console.log(`🖼️ ${media.length} medya yükleniyor...`);

        // Çoklu medya yükle (video + resim karışık)
        const uploadResults = await uploadMultipleMedia(media, currentUser.uid);

        // Başarısız yüklemeleri kontrol et
        const failedUploads = uploadResults.filter(r => !r.success);
        if (failedUploads.length > 0) {
          setIsPosting(false);
          alert(t('dashboard.errors.mediaUploadFailed', {
            count: failedUploads.length,
            error: failedUploads[0].error
          }));
          return;
        }

        // Video ve resimleri ayır
        const videos = uploadResults.filter(r => r.type === 'video');
        const images = uploadResults.filter(r => r.type !== 'video');

        // URL'leri topla
        imageUrls = images.map(r => r.url);
        uploadedFilePaths = uploadResults.map(r => r.path); // Storage path'leri kaydet

        // Video kontrolü (şimdilik tek video)
        if (videos.length > 0) {
          videoUrl = videos[0].url;
          if (videos.length > 1) {
            console.warn('⚠️ Çoklu video desteği yok, sadece ilk video kullanılacak');
          }
        }

        // Backward compatibility
        if (imageUrls.length > 0) {
          imageUrl = imageUrls[0];
        }

        console.log(`✅ ${uploadResults.length} medya yüklendi:`, {
          images: imageUrls.length,
          videos: videos.length,
          videoUrl
        });
      }

      // 2. Platform bilgilerini hazırla
      const platformsToPost = [];

      connectedSelectedPlatforms.forEach(platformId => {
        const platformData = connectedPlatforms.find(p => p.id === platformId);
        const custom = platformCustomization[platformId];

        console.log('🔍 DEBUG - Platform hazırlanıyor:', {
          platformId,
          hasCustom: !!custom,
          customPostTypes: custom?.postTypes,
          platformData: platformData
        });

        // Instagram için: hem feed hem story seçiliyse iki ayrı post oluştur
        if (platformId === 'instagram' && custom && custom.postTypes && custom.postTypes.length > 0) {
          console.log('✅ Instagram ÖZEL işlem - postTypes:', custom.postTypes);
          custom.postTypes.forEach(postType => {
            const preparedData = {
              id: platformId,
              name: platformData.name || platformId,
              token: platformData.token,
              userId: platformData.userId,
              pageId: platformData.pageId,
              pageName: platformData.pageName,
              username: platformData.username,
              postType: postType,
              selectedStoryMediaIndex: custom.selectedStoryMediaIndex || 0,
              displayName: `${platformData.name} (${postType === 'feed' ? t('dashboard.feedPost') : t('dashboard.storyPost')})`
            };

            console.log('📋 Instagram platform data prepared:', postType, preparedData);
            platformsToPost.push(preparedData);
          });
        } else {
          console.log('⚠️ Instagram NORMAL işlem - postType gönderilmeyecek!');
          // Diğer platformlar için normal işlem
          const preparedData = {
            id: platformId,
            name: platformData.name || platformId,
            token: platformData.token,
            userId: platformData.userId,
            pageId: platformData.pageId,
            pageName: platformData.pageName,
            username: platformData.username,
            displayName: platformData.name || platformId
          };

          console.log('📋 Platform data prepared:', platformId, preparedData);
          platformsToPost.push(preparedData);
        }
      });

      // 3. Tüm platformlara post at
      setPostingProgress(t('dashboard.posting.sharing', { count: platformsToPost.length }));
      console.log('📤 Post atılıyor:', platformsToPost.length, 'platform');
      console.log('📝 Post content:', content);
      console.log('🖼️ Image URLs:', imageUrls);
      console.log('🎥 Video URL:', videoUrl);
      const results = await postToMultiplePlatforms({
        content: content,
        imageUrl: imageUrl,
        imageUrls: imageUrls,
        videoUrl: videoUrl
      }, platformsToPost);

      // 4. Sonuçları state'e kaydet (modal içinde göstereceğiz)
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      setPostingResults({
        successful,
        failed,
        total: results.length
      });

      // 5. Başarılı paylaşımlar varsa toplam sayacı güncelle
      if (successful.length > 0) {
        setTotalPosts(prev => prev + successful.length);
      }

      // 6. BAŞARILI PAYLAŞIMLAR varsa Storage'dan resimleri SİL
      // Instagram/Facebook resimleri kendi serverlarına aldı, artık bizde tutmaya gerek yok
      if (successful.length > 0 && uploadedFilePaths.length > 0) {
        console.log('🗑️ Storage temizleniyor...', uploadedFilePaths);
        // Async olarak sil, kullanıcıyı bekletme
        deleteMultipleImages(uploadedFilePaths).then(() => {
          console.log('✅ Storage temizlendi!');
        }).catch((error) => {
          console.error('⚠️ Storage temizleme hatası:', error);
          // Hata olsa bile kullanıcıya gösterme, önemli değil
        });
      }

    } catch (error) {
      console.error('Post hatası:', error);
      setPostingResults({
        successful: [],
        failed: [],
        total: 0,
        error: error.message
      });

      // Hata olursa da Storage'ı temizle (resimleri yükledik ama paylaşamadık)
      if (uploadedFilePaths && uploadedFilePaths.length > 0) {
        console.log('🗑️ Hata nedeniyle Storage temizleniyor...', uploadedFilePaths);
        deleteMultipleImages(uploadedFilePaths).catch((err) => {
          console.error('⚠️ Storage temizleme hatası:', err);
        });
      }
    }
  };

  // Şablon fonksiyonları
  const loadTemplates = async () => {
    console.log('📥 loadTemplates çağrıldı, user ID:', currentUser.uid);
    const result = await getAllTemplates(currentUser.uid);
    console.log('📦 getAllTemplates sonucu:', result);
    if (result.success) {
      console.log('✅ Şablonlar yüklendi, toplam:', result.data.length);
      console.log('📋 Şablonlar:', result.data);
      setTemplates(result.data);
    } else {
      console.error('❌ Şablon yükleme hatası:', result.error);
    }
  };

  const loadTotalPosts = async () => {
    if (!currentUser) return;

    try {
      const postsRef = collection(db, 'users', currentUser.uid, 'posts');
      const postsQuery = query(postsRef);
      const querySnapshot = await getDocs(postsQuery);
      setTotalPosts(querySnapshot.size);
      console.log('📊 Toplam paylaşım:', querySnapshot.size);
    } catch (error) {
      console.error('❌ Toplam paylaşım yükleme hatası:', error);
      setTotalPosts(0);
    }
  };

  const handleSaveTemplate = async () => {
    console.log('🔵 handleSaveTemplate çağrıldı');
    console.log('Template adı:', templateName);
    console.log('Content:', content);
    console.log('Media:', media);
    console.log('Current user:', currentUser);

    if (!templateName.trim()) {
      alert(t('dashboard.errors.templateNameRequired'));
      return;
    }

    if (!content.trim() && media.length === 0) {
      alert(t('dashboard.errors.templateContentRequired'));
      return;
    }

    const templateData = {
      name: templateName,
      content: content,
      media: media,
      platformCustomization: platformCustomization
    };

    console.log('📦 Template data hazırlandı:', templateData);
    console.log('User ID:', currentUser.uid);

    try {
      const result = await saveTemplate(currentUser.uid, templateData);
      console.log('✅ saveTemplate sonucu:', result);

      if (result.success) {
        alert(t('dashboard.success.templateSaved', { name: templateName }));
        setTemplateName('');
        setShowSaveTemplateDialog(false);
        await loadTemplates();
      } else {
        alert(t('dashboard.errors.templateSaveFailed', { error: result.error }));
      }
    } catch (error) {
      console.error('❌ handleSaveTemplate hatası:', error);
      alert(t('dashboard.errors.errorOccurred', { message: error.message }));
    }
  };

  const handleLoadTemplate = (template) => {
    // İçeriği yükle
    setContent(template.content || '');
    setMedia(template.media || []);

    // Platform özelleştirmelerini yükle
    if (template.platformCustomization) {
      setPlatformCustomization(template.platformCustomization);
    }

    alert(t('dashboard.success.templateLoaded', { name: template.name }));
  };

  const handleDeleteTemplate = async (templateId) => {
    console.log('🗑️ handleDeleteTemplate çağrıldı, template ID:', templateId);

    if (!window.confirm(t('dashboard.warnings.confirmDeleteTemplate'))) {
      console.log('❌ Kullanıcı silmeyi iptal etti');
      return;
    }

    console.log('🔥 Şablon siliniyor...');
    const result = await deleteTemplate(currentUser.uid, templateId);
    console.log('📦 deleteTemplate sonucu:', result);

    if (result.success) {
      alert(t('dashboard.success.templateDeleted'));
      console.log('♻️ Şablonlar yeniden yükleniyor...');
      await loadTemplates();
    } else {
      alert(t('dashboard.errors.templateDeleteFailed', { error: result.error }));
    }
  };

  // AI ile oluşturulan içeriği kullan
  const handleUseAIContent = (aiGeneratedText) => {
    setContent(aiGeneratedText);
    console.log('✅ AI içeriği ana metin alanına eklendi');
  };

  // Çıkış yap
  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      // Tam sayfa yenilemesi ile login sayfasına git
      window.location.href = '/login';
    } else {
      alert(t('dashboard.errors.logoutFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/logo.svg"
                alt="Social Octo Logo"
                className="w-10 h-10"
              />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Social Octo
                </h1>
                <p className="text-xs text-gray-500">{t('common.tagline')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-semibold text-gray-700">
                  {currentUser?.displayName || currentUser?.email}
                </span>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition"
              >
                <Settings className="w-5 h-5" />
                {t('nav.settings')}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:border-red-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                title={t('nav.logout')}
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Sol Taraf - İçerik Oluşturma */}
          <div className="lg:col-span-2 space-y-6">

            {/* İçerik Kartı */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Send className="w-6 h-6 text-blue-600" />
                {t('dashboard.newPost')}
              </h2>

              {/* Metin Alanı */}
              <div className="mb-4">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={t('dashboard.postPlaceholder')}
                  className="w-full h-40 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none resize-none text-gray-800"
                />
                <p className="text-sm text-gray-500 mt-1">{t('common.characterCount', { count: content.length })}</p>
              </div>

              {/* Medya Yükleme ve AI Butonu */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('dashboard.mediaLabel')}</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleMediaUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 px-4 py-2 rounded-lg transition">
                      <ImageIcon className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700">{t('dashboard.addImage')}</span>
                    </div>
                  </label>
                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleMediaUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 bg-purple-100 hover:bg-purple-200 px-4 py-2 rounded-lg transition">
                      <Video className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-700">{t('dashboard.addVideo')}</span>
                    </div>
                  </label>

                  {/* AI ile Metin Üret Butonu */}
                  <button
                    onClick={() => setShowAIModal(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-4 py-2 rounded-lg transition text-white shadow-md hover:shadow-lg"
                  >
                    <Sparkles className="w-5 h-5" />
                    <span className="text-sm font-semibold">{t('dashboard.aiGenerate')}</span>
                  </button>
                </div>

                {/* Medya Önizleme */}
                {media.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {media.map((item, index) => (
                      <div key={index} className="relative group">
                        {item.type === 'video' ? (
                          <div className="relative">
                            <video
                              src={item.url}
                              className="w-full h-32 object-cover rounded-lg bg-black"
                              controls
                              preload="metadata"
                            />
                          </div>
                        ) : (
                          <img src={item.url} alt={`Medya ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                        )}
                        <button
                          onClick={() => removeMedia(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition"
                        >
                          ×
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          {item.type === 'video' ? '🎥' : '📷'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Zamanla Paylaş */}
              <div className="border-t-2 border-gray-100 pt-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-700">{t('dashboard.schedulePost')}</span>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">{t('dashboard.comingSoon')}</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked={scheduleEnabled}
                      onChange={(e) => setScheduleEnabled(e.target.checked)}
                      disabled
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-blue-600 peer-disabled:opacity-50 transition-all"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-5"></div>
                  </label>
                </div>
                {scheduleEnabled && (
                  <p className="text-xs text-gray-500 mt-2">{t('dashboard.comingSoonMessage')}</p>
                )}
              </div>

              {/* Şablon Olarak Kaydet */}
              <div className="border-t-2 border-gray-100 pt-4">
                <button
                  onClick={() => setShowSaveTemplateDialog(true)}
                  disabled={!content.trim() && media.length === 0}
                  className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  {t('dashboard.saveAsTemplate')}
                </button>
              </div>
            </div>

            {/* Platformlar - Accordion Tarzı */}
            {connectedPlatforms.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">{t('dashboard.platforms')}</h2>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const connectedIds = connectedPlatforms.filter(p => p.enabled && p.token).map(p => p.id);
                        if (selectedPlatforms.length === connectedIds.length) {
                          // Tümünü kaldır
                          setSelectedPlatforms([]);
                        } else {
                          // Tümünü seç
                          setSelectedPlatforms(connectedIds);
                        }
                      }}
                      className="text-xs bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-200 transition font-medium"
                    >
                      {selectedPlatforms.length === connectedPlatforms.filter(p => p.enabled && p.token).length ?
                        t('dashboard.deselectAll') : t('dashboard.selectAll')}
                    </button>
                    <span className="text-sm text-gray-500">
                      {connectedPlatforms.filter(p => p.enabled && p.token).length} / {connectedPlatforms.length} {t('dashboard.connected')}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {t('dashboard.selectPlatforms')}
                </p>

                <div className="space-y-3">
                  {console.log('📋 Accordion render - connectedPlatforms:', connectedPlatforms.length, 'platform', connectedPlatforms.map(p => p.id))}
                  {connectedPlatforms.map(userPlatform => {
                    const platformInfo = allPlatforms.find(p => p.id === userPlatform.id);
                    const isConnected = userPlatform.enabled && userPlatform.token;
                    const isExpanded = expandedPlatforms.includes(userPlatform.id);
                    const custom = platformCustomization[userPlatform.id] || {
                      useMainContent: true,
                      customContent: '',
                      useMainImages: true,
                      customImages: []
                    };

                    if (!platformInfo) {
                      console.warn('⚠️ Platform bilgisi bulunamadı:', userPlatform.id, 'allPlatforms:', allPlatforms.map(p => p.id));
                      return null;
                    }

                    // Ana içeriği al
                    const displayContent = custom.useMainContent ? content : custom.customContent;
                    const displayMedia = custom.useMainImages ? media : custom.customImages;

                    return (
                      <div key={userPlatform.id} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                        {/* Platform Header - Tıklanabilir */}
                        <div
                          onClick={() => togglePlatformExpand(userPlatform.id)}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            {/* Checkbox - Platform Seçimi */}
                            {isConnected && (
                              <input
                                type="checkbox"
                                checked={selectedPlatforms.includes(userPlatform.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePlatform(userPlatform.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                              />
                            )}
                            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg p-1.5 shadow-sm">
                              <img
                                src={platformInfo.logo}
                                alt={platformInfo.name}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className="text-left">
                              <h3 className="text-sm font-bold text-gray-800">{platformInfo.name}</h3>
                              {!isConnected && (
                                <p className="text-xs text-orange-600 font-medium">⚠ {t('dashboard.notConnected')}</p>
                              )}
                              {isConnected && userPlatform.username && (
                                <p className="text-xs text-gray-500">@{userPlatform.username}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {t('dashboard.maxCharacters', { count: platformInfo.id === 'twitter' ? 280 : 2200 })}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>

                        {/* Platform İçeriği - Accordion */}
                        {isExpanded && (
                          <div className="border-t-2 border-gray-200 p-4 bg-gray-50">

                            {/* Bağlı değilse uyarı */}
                            {!isConnected && (
                              <div className="mb-4 bg-orange-50 border-2 border-orange-200 rounded-lg p-3">
                                <p className="text-sm text-orange-800 mb-2">
                                  {t('dashboard.platformNotConnectedWarning')}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConnectPlatform(userPlatform.id);
                                  }}
                                  className="text-sm bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition font-semibold"
                                >
                                  {t('dashboard.connectAccount')}
                                </button>
                              </div>
                            )}

                            {/* Metin Alanı */}
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <input
                                  type="checkbox"
                                  id={`main-content-${userPlatform.id}`}
                                  checked={custom.useMainContent}
                                  onChange={() => togglePlatformContent(userPlatform.id, 'useMainContent')}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`main-content-${userPlatform.id}`} className="text-sm font-semibold text-gray-700 cursor-pointer">
                                  {t('dashboard.useMainContent')}
                                </label>
                              </div>

                              {custom.useMainContent ? (
                                <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                    {content || <span className="text-gray-400 italic">{t('dashboard.noTextEntered')}</span>}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-2">{t('common.characterCount', { count: content.length })}</p>
                                </div>
                              ) : (
                                <textarea
                                  value={custom.customContent}
                                  onChange={(e) => updateCustomContent(userPlatform.id, e.target.value)}
                                  placeholder={t('dashboard.customTextPlaceholder', { platform: platformInfo.name })}
                                  className="w-full h-32 p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none resize-none text-sm"
                                />
                              )}
                            </div>

                            {/* Görseller/Videolar */}
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <input
                                  type="checkbox"
                                  id={`main-media-${userPlatform.id}`}
                                  checked={custom.useMainImages}
                                  onChange={() => togglePlatformContent(userPlatform.id, 'useMainImages')}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`main-media-${userPlatform.id}`} className="text-sm font-semibold text-gray-700 cursor-pointer">
                                  {t('dashboard.useMainMedia')}
                                </label>
                              </div>

                              {custom.useMainImages ? (
                                // Ana medya gösterimi
                                media.length > 0 ? (
                                  <div className="grid grid-cols-3 gap-2">
                                    {media.map((item, index) => (
                                      <div key={index} className="relative">
                                        {item.type === 'video' ? (
                                          <video
                                            src={item.url}
                                            className="w-full h-20 object-cover rounded-lg bg-black"
                                            controls
                                            preload="metadata"
                                          />
                                        ) : (
                                          <img src={item.url} alt={`Medya ${index + 1}`} className="w-full h-20 object-cover rounded-lg" />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                    <p className="text-sm text-gray-400">{t('dashboard.noMediaAdded')}</p>
                                  </div>
                                )
                              ) : (
                                // Özel medya yükleme ve gösterimi
                                <div>
                                  <label className="cursor-pointer inline-block">
                                    <input
                                      type="file"
                                      accept="image/*,video/*"
                                      multiple
                                      onChange={(e) => handleCustomMediaUpload(userPlatform.id, e)}
                                      className="hidden"
                                    />
                                    <div className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition font-semibold">
                                      <ImageIcon className="w-4 h-4" />
                                      <Video className="w-4 h-4" />
                                      <span>{t('dashboard.uploadMediaButton')}</span>
                                    </div>
                                  </label>

                                  {/* Özel medya önizleme */}
                                  {custom.customImages.length > 0 && (
                                    <div className="mt-3 grid grid-cols-3 gap-2">
                                      {custom.customImages.map((item, index) => (
                                        <div key={index} className="relative group">
                                          {item.type === 'video' ? (
                                            <video
                                              src={item.url}
                                              className="w-full h-20 object-cover rounded-lg bg-black"
                                              controls
                                              preload="metadata"
                                            />
                                          ) : (
                                            <img src={item.url} alt={`Özel ${index + 1}`} className="w-full h-20 object-cover rounded-lg" />
                                          )}
                                          <button
                                            onClick={() => removeCustomMedia(userPlatform.id, index)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition text-xs"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Instagram Post Type Selection */}
                            {userPlatform.id === 'instagram' && (
                              <div className="mb-4 border-t-2 border-gray-200 pt-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                  {t('dashboard.instagramPostType')}
                                </label>
                                <div className="space-y-2">
                                  <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition">
                                    <input
                                      type="checkbox"
                                      checked={(custom.postTypes || ['feed']).includes('feed')}
                                      onChange={() => toggleInstagramPostType(userPlatform.id, 'feed')}
                                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-2xl">📰</span>
                                    <div className="flex-1">
                                      <div className="font-semibold text-gray-800">{t('dashboard.feedPost')}</div>
                                      <div className="text-xs text-gray-500">{t('dashboard.feedPostDescription')}</div>
                                    </div>
                                  </label>

                                  <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition">
                                    <input
                                      type="checkbox"
                                      checked={(custom.postTypes || ['feed']).includes('story')}
                                      onChange={() => toggleInstagramPostType(userPlatform.id, 'story')}
                                      className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="text-2xl">⚡</span>
                                    <div className="flex-1">
                                      <div className="font-semibold text-gray-800">{t('dashboard.storyPost')}</div>
                                      <div className="text-xs text-gray-500">{t('dashboard.storyPostDescription')}</div>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            )}

                            {/* Story Aspect Ratio Warning */}
                            {userPlatform.id === 'instagram' && (custom.postTypes || ['feed']).includes('story') && (
                              <div className="mb-4 bg-orange-50 border-2 border-orange-200 rounded-lg p-3">
                                <p className="text-sm text-orange-800">
                                  <strong>💡 İpucu:</strong> {t('dashboard.storyAspectWarning')}
                                </p>
                              </div>
                            )}

                            {/* Story Media Selector (multi-media) */}
                            {userPlatform.id === 'instagram' &&
                             (custom.postTypes || ['feed']).includes('story') &&
                             displayMedia.length > 1 && (
                              <div className="mb-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                                <p className="text-sm font-semibold text-blue-800 mb-3">
                                  {t('dashboard.selectMediaForStory')}
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                  {displayMedia.map((item, index) => (
                                    <label key={index} className="cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`story-media-${userPlatform.id}`}
                                        checked={(custom.selectedStoryMediaIndex || 0) === index}
                                        onChange={() => updateStoryMediaIndex(userPlatform.id, index)}
                                        className="sr-only"
                                      />
                                      <div className={`relative border-2 rounded-lg overflow-hidden transition ${
                                        (custom.selectedStoryMediaIndex || 0) === index
                                          ? 'border-blue-600 ring-2 ring-blue-300'
                                          : 'border-gray-300 hover:border-blue-400'
                                      }`}>
                                        {item.type === 'video' ? (
                                          <video src={item.url} className="w-full h-24 object-cover" />
                                        ) : (
                                          <img src={item.url} alt={`Story ${index + 1}`} className="w-full h-24 object-cover" />
                                        )}
                                        {(custom.selectedStoryMediaIndex || 0) === index && (
                                          <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center">
                                            ✓
                                          </div>
                                        )}
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Önizleme Butonu */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(userPlatform.id);
                              }}
                              className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition font-semibold"
                            >
                              <Eye className="w-4 h-4" />
                              {t('dashboard.platformPreview', { platform: platformInfo.name })}
                            </button>

                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Paylaş Butonu */}
            <button
              onClick={handlePost}
              disabled={selectedPlatforms.length === 0}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Send className="w-6 h-6" />
              {t('dashboard.share', { count: selectedPlatforms.length })}
            </button>

          </div>

          {/* Sağ Taraf - Platformlar */}
          <div className="space-y-6">

            {/* Şablonlarım */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {t('dashboard.templates.title')}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{t('dashboard.templates.count', { count: templates.length })}</span>
                  <button
                    onClick={() => {
                      console.log('🔄 Manuel yenileme butonu tıklandı');
                      loadTemplates();
                    }}
                    className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded hover:bg-blue-200 transition"
                  >
                    {t('dashboard.templates.refresh')}
                  </button>
                </div>
              </div>

              {console.log('🎨 Şablonlar render, templates state:', templates)}
              {templates.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-2">📄</div>
                  <p className="text-sm text-gray-500">{t('dashboard.templates.noTemplates')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('dashboard.templates.noTemplatesHint')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {console.log('🗂️ Şablonlar map ediliyor, toplam:', templates.length)}
                  {templates.map((template, index) => {
                    console.log(`📄 Şablon ${index + 1}:`, template);
                    return (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onLoad={handleLoadTemplate}
                        onDelete={handleDeleteTemplate}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Platformlar */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('dashboard.connectedPlatforms.title')}</h3>

              {connectedPlatforms.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-5xl mb-3">🔗</div>
                  <p className="text-gray-600 text-sm mb-4">{t('dashboard.connectedPlatforms.noPlatforms')}</p>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition text-sm font-semibold"
                  >
                    {t('dashboard.connectedPlatforms.addPlatform')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Platform İkonları Grid */}
                  <div className="grid grid-cols-4 gap-3">
                    {connectedPlatforms.map(userPlatform => {
                      const platformInfo = allPlatforms.find(p => p.id === userPlatform.id);
                      if (!platformInfo) return null;

                      const isConnected = userPlatform.enabled && userPlatform.token;

                      return (
                        <div key={userPlatform.id} className="flex flex-col items-center gap-1">
                          <div className="relative">
                            <div className={`p-3 rounded-xl transition-all bg-gradient-to-br flex items-center justify-center ${
                              isConnected
                                ? 'from-green-100 to-green-200 border-2 border-green-500 shadow-md'
                                : 'from-gray-100 to-gray-200 border-2 border-gray-300 shadow'
                            }`}>
                              <img
                                src={platformInfo.logo}
                                alt={platformInfo.name}
                                className="w-10 h-10 object-contain"
                              />
                            </div>
                            {!isConnected && (
                              <div className="absolute -top-1 -right-1 group">
                                <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white shadow">
                                  <span className="text-white text-xs font-bold">!</span>
                                </div>
                                <div className="absolute top-6 right-0 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  {t('dashboard.notConnected')}
                                </div>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-700 text-center truncate w-full font-medium">
                            {platformInfo.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Daha Fazla Platform Ekle */}
                  <button
                    onClick={() => setShowSettings(true)}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition text-sm font-semibold"
                  >
                    {t('dashboard.connectedPlatforms.addMore')}
                  </button>
                </div>
              )}
            </div>

            {/* İstatistikler */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('dashboard.stats.title')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('dashboard.stats.connectedPlatforms')}</span>
                  <span className="text-2xl font-bold text-blue-600">{connectedPlatforms.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('dashboard.stats.selectedPlatforms')}</span>
                  <span className="text-2xl font-bold text-purple-600">{selectedPlatforms.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('dashboard.stats.totalPosts')}</span>
                  <span className="text-2xl font-bold text-pink-600">{totalPosts}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Modals */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => {
          setShowSettings(false);
          // Modal kapandığında platformları yeniden yükle
          loadConnectedPlatforms();
        }}
        onPlatformsUpdate={handlePlatformsUpdate}
      />

      {previewPlatform && (
        <PlatformPreviewModal
          isOpen={!!previewPlatform}
          onClose={() => setPreviewPlatform(null)}
          platform={previewPlatform}
          content={getContentForPlatform(previewPlatform.id).content}
          mediaItems={getContentForPlatform(previewPlatform.id).images}
          currentUser={currentUser}
        />
      )}

      {/* AI Content Modal */}
      <AIContentModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        mediaFiles={media}
        onUseContent={handleUseAIContent}
        connectedPlatforms={connectedPlatforms.filter(p => p.enabled && p.token)}
      />

      {/* Instagram Setup Modal */}
      <InstagramSetupModal
        isOpen={showInstagramSetup}
        onClose={() => {
          setShowInstagramSetup(false);
          setInstagramSetupError(null);
        }}
        errorType={instagramSetupError?.errorType}
        errorDetails={instagramSetupError}
      />

      {/* Şablon Kaydet Dialog */}
      {showSaveTemplateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Save className="w-6 h-6 text-green-600" />
                {t('dashboard.templates.saveDialog.title')}
              </h2>
              <button
                onClick={() => {
                  setShowSaveTemplateDialog(false);
                  setTemplateName('');
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('dashboard.templates.saveDialog.nameLabel')}
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('dashboard.templates.saveDialog.namePlaceholder')}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTemplate();
                  }
                }}
              />
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                {t('dashboard.templates.saveDialog.hint')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveTemplateDialog(false);
                  setTemplateName('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-semibold"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading/Results Modal - Posting Progress */}
      {isPosting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">

            {/* Loading State */}
            {!postingResults && (
              <div className="text-center">
                {/* Spinner */}
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>

                {/* Progress Message */}
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t('dashboard.posting.sharingTitle')}
                </h3>
                <p className="text-gray-600 text-lg whitespace-pre-line">
                  {postingProgress}
                </p>

                {/* Info */}
                <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    {t('dashboard.posting.pleaseWait')}
                  </p>
                </div>
              </div>
            )}

            {/* Results State */}
            {postingResults && (
              <div>
                {/* Başlık */}
                <div className="text-center mb-6">
                  {postingResults.error ? (
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-4xl">❌</span>
                    </div>
                  ) : postingResults.successful.length === postingResults.total ? (
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-4xl">✅</span>
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-4xl">⚠️</span>
                    </div>
                  )}

                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {postingResults.error ? t('dashboard.posting.errorTitle') :
                     postingResults.successful.length === postingResults.total ? t('dashboard.posting.success') :
                     postingResults.successful.length > 0 ? t('dashboard.posting.partialSuccess') : t('dashboard.posting.failed')}
                  </h3>
                </div>

                {/* Genel hata */}
                {postingResults.error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800 text-sm">
                      <strong>{t('dashboard.posting.errorLabel')}:</strong> {postingResults.error}
                    </p>
                  </div>
                )}

                {/* Başarılı platformlar */}
                {postingResults.successful.length > 0 && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-green-800 font-semibold mb-2">
                      {t('dashboard.posting.successful', { count: postingResults.successful.length })}
                    </p>
                    <ul className="text-green-700 text-sm space-y-1">
                      {postingResults.successful.map((result, idx) => (
                        <li key={idx}>• {result.platformName}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Başarısız platformlar */}
                {postingResults.failed.length > 0 && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800 font-semibold mb-2">
                      {t('dashboard.posting.failedCount', { count: postingResults.failed.length })}
                    </p>
                    <ul className="text-red-700 text-sm space-y-1">
                      {postingResults.failed.map((result, idx) => (
                        <li key={idx}>
                          <strong>• {result.platformName}:</strong> {result.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Butonlar */}
                <div className="mt-6">
                  <button
                    onClick={() => {
                      // Başarılı paylaşım varsa formu temizle (ama platformları KORU)
                      if (postingResults.successful.length > 0) {
                        setContent('');
                        setMedia([]);
                        // Platform seçimlerini temizleme - kullanıcı tekrar paylaşım yapabilsin
                      }
                      // Modal'ı kapat
                      setIsPosting(false);
                      setPostingResults(null);
                      setPostingProgress('');
                    }}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                  >
                    {t('common.ok')}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
